import { InputNumber, Slider } from 'antd';
import { useSearchParams } from 'react-router-dom';
import Notifications from '../components/Notifications';
import useNotificationStore from '../store/notificationStore';
import usePersistentStore from '../store/persistentStore';

const EmbedInfographicConfigurator = () => {
  const [searchParams] = useSearchParams();
  const iframeHeight = searchParams.get('height')
  const iframeWidth = searchParams.get('width')
  
  // Use persistent store for scale
  const persistentStore = usePersistentStore();
  const scale = usePersistentStore((state) => state.get('embedTestScale')) || 0.5;
  
  // Always use the root URL of the server where the page is running
  const serverRootUrl = window.location.origin;
  
  // Notification store for toaster messages
  const addMessage = useNotificationStore((state) => state.addMessage);
  
  const setScale = (newScale) => {
    persistentStore.set('embedTestScale')(newScale);
  };
  
  // Get all query parameters and convert them to a string
  const allParams = searchParams.toString();
  
  // Construct the iframe src URL with server root URL prefix
  const iframeSrc = `${serverRootUrl}/embed/ig?${allParams}`;



  // Generate the HTML code for the embeddable div
  const generateEmbedCode = () => {
    const embedCode = `<div
  style="
    width: ${iframeWidth * scale}px;
    height: ${iframeHeight * scale}px;
    overflow: hidden;
  ">
  <div
    style="
      transform: scale(${scale});
      transform-origin: top left;
      width: ${iframeWidth}px;
      height: ${iframeHeight}px;
    ">
    <iframe
      src="${serverRootUrl}/embed/ig?${allParams}"
      width="${iframeWidth}"
      height="${iframeHeight}"
      title="Embedded Infographic"
      style="border: none; width: 100%; height: 100%;"
    />
  </div>
</div>`;
    return embedCode;
  };

  const handleScaleChange = (newScale) => {
    const scaleValue = parseFloat(newScale);
    if (!isNaN(scaleValue) && scaleValue > 0 && scaleValue <= 2) {
      setScale(scaleValue);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      addMessage({ text: 'Code copied to clipboard!', type: 'success' });
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generateEmbedCode();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      addMessage({ text: 'Code copied to clipboard!', type: 'success' });
    }
  };

  const copyUrlToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(iframeSrc);
      addMessage({ text: 'URL copied to clipboard!', type: 'success' });
    } catch (err) {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = iframeSrc;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      addMessage({ text: 'URL copied to clipboard!', type: 'success' });
    }
  };

  return (
    <>
      <Notifications />
      <div className="min-h-screen bg-gray-100 p-8 font-sans">
        <div className="w-fit max-w-none mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4 mt-0">
            Embed Configurator
          </h1>
          <p className="text-gray-600 mb-6 text-sm">
            This page shows how the infographic will appear when embedded in an iframe on external websites.
          </p>
          
          {/* Settings */}
          <h3 className="text-lg font-semibold text-gray-700 mt-8 mb-4 my-0">Settings:</h3>
          <div className="mb-6 border borer-red-500 border-solid border-1 p-4 rounded max-w-6xl">
            <div className="grid grid-cols-[auto,1fr] gap-2 items-center">
              <span>Scale:</span>
              <div className="flex items-center gap-3">
                <Slider
                  min={0.1}
                  max={2}
                  step={0.05}
                  value={scale}
                  onChange={handleScaleChange}
                  style={{ flex: 1, cursor: 'pointer' }}
                  tooltip={{ formatter: (value) => `${value}` }}
                />
                <InputNumber
                  min={0.1}
                  max={2}
                  step={0.05}
                  value={scale}
                  onChange={handleScaleChange}
                  style={{ width: 80, cursor: 'pointer' }}
                  precision={2}
                />
              </div>
              <div className="col-span-full text-sm text-gray-500">
                Original iframe dimensions: {iframeWidth}px × {iframeHeight}px
                <span className="mx-2">·</span>
                Scaled dimensions: {Math.round(iframeWidth * scale)}px × {Math.round(iframeHeight * scale)}px
              </div>
            </div>
          </div>
          
          {/* Preview */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Preview:</h3>
            <div className="border borer-red-500 border-solid border-1 p-4 rounded">
              <div 
                className="flex justify-center"
                dangerouslySetInnerHTML={{ __html: generateEmbedCode() }}
              />
            </div>
          </div>

          {/* Code Preview */}
          <div className="mt-10 max-w-6xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700 my-0">IFRAME source code:</h3>
            </div>
            <p className="text-sm text-gray-600 flex items-center justify-between my-0 mb-2">
              Copy this code and paste it into your website to embed the infographic:
              <button
                onClick={copyToClipboard}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer shrink-0 ml-2"
              >
                Copy Code
              </button>
            </p>
            <div className="bg-gray-900 rounded p-4">
              <pre className="text-gray-200 text-xs overflow-x-auto my-0 py-0">
                <code>{generateEmbedCode()}</code>
              </pre>
            </div>
          </div>

          <div className="mt-10 max-w-6xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-700 my-0">Source URL:</h3>
              <button
                onClick={copyUrlToClipboard}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer shrink-0"
              >
                Copy URL
              </button>
            </div>
            <code className="text-sm text-gray-600 break-all bg-gray-100 p-2 rounded block">
              {iframeSrc}
            </code>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default EmbedInfographicConfigurator;
